import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  AccessibleCompanies,
  DefaultApi,
  ReferenceSiteIdValue,
  SiteBody,
  SiteItem,
  SuccinctSite,
} from "@vviinn/user-managment-client-ts";
import { FetchableState } from "..";
import { serializeModel, ApiRequest } from "../../../api";
import { CompanyRequest, MandatoryCompanyId } from "../../../company";
import { defaultSitesInDetailsParameters } from "../../../site";

type SitesState = FetchableState & {
  sites: SuccinctSite[];
  accessibleCompanies: AccessibleCompanies[];
  referenceSiteId?: ReferenceSiteIdValue;
  upsertingSite: boolean;
};

const initialState: SitesState = {
  sites: [],
  accessibleCompanies: [],
  referenceSiteId: undefined,
  loading: false,
  upsertingSite: false,
  error: false,
  status: "idle",
};

export const fetchSuccinctSites = createAsyncThunk(
  "sites/fetchSuccinct",
  async ({ api, value }: ApiRequest<MandatoryCompanyId>) => {
    const response = await api.getCompanySites(value);
    const data = response.value;
    return data.map(serializeModel);
  },
);

export const fetchDetailedSites = createAsyncThunk(
  "sites/fetchDetailed",
  async ({ api, value }: ApiRequest<MandatoryCompanyId>) => {
    const response = await api.getCompanySitesByParametersInDetails(
      value,
      defaultSitesInDetailsParameters,
    );
    const data = response.value;
    return data.map(serializeModel);
  },
);

type AddSiteRequest = CompanyRequest & { siteBody: SiteBody };

export const upsertSite = createAsyncThunk(
  "sites/add",
  async ({ api, value }: ApiRequest<AddSiteRequest>) => {
    const { siteBody, companyId } = value;
    const response = await api.upsertCompanySite(companyId, siteBody);
    return serializeModel(response.value);
  },
);

type DeleteSiteRequest = CompanyRequest & { siteId: SiteItem["id"] };

export const deleteSite = createAsyncThunk(
  "sites/delete",
  async ({ api, value }: ApiRequest<DeleteSiteRequest>) => {
    const { siteId, companyId } = value;
    await api.deleteCompanySite(companyId, siteId);

    return siteId;
  },
);

export const fetchAccessibleSites = createAsyncThunk(
  "sites/getAcessibleSites",
  async (api: DefaultApi) => {
    const response = await api.getUserAccessibleSites();
    const data = response.value;
    if (!data) return [];
    return serializeModel(data);
  },
);

type ReferenceSiteIdRequest = CompanyRequest & { siteId: SiteItem["id"] };

export const fetchReferenceSiteId = createAsyncThunk(
  "sites/getReferenceSiteId",
  async ({ api, value }: ApiRequest<ReferenceSiteIdRequest>) => {
    const { siteId, companyId } = value;
    const response = await api.getReferenceSiteId(companyId, siteId);
    return serializeModel(response.value);
  },
);

const sitesSlice = createSlice({
  name: "sites",
  initialState,
  reducers: {
    resetSites(state) {
      state.sites = initialState.sites;
      state.status = "idle";
      state.error = false;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchSuccinctSites.pending, (state) => {
      state.loading = true;
      state.error = false;
      state.status = "loading";
    });
    builder.addCase(fetchSuccinctSites.rejected, (state) => {
      state.loading = false;
      state.error = true;
      state.status = "error";
      state.sites = initialState.sites;
    });
    builder.addCase(fetchSuccinctSites.fulfilled, (state, { payload }) => {
      state.loading = false;
      state.error = false;
      state.sites = payload;
      state.status = "fulfilled";
    });

    builder.addCase(fetchAccessibleSites.fulfilled, (state, { payload }) => {
      state.accessibleCompanies = payload;
    });

    builder.addCase(fetchReferenceSiteId.fulfilled, (state, { payload }) => {
      state.referenceSiteId = payload;
    });

    builder.addCase(upsertSite.pending, (state) => {
      state.upsertingSite = true;
    });
    builder.addCase(upsertSite.rejected, (state) => {
      state.upsertingSite = false;
    });
    builder.addCase(upsertSite.fulfilled, (state, { payload }) => {
      state.upsertingSite = false;
      const siteIndex = state.sites.findIndex((site) => site.id === payload.id);
      if (siteIndex === -1) {
        state.sites.push(payload);
      } else {
        state.sites[siteIndex] = payload;
      }
    });

    builder.addCase(deleteSite.fulfilled, (state, { payload }) => {
      state.sites = state.sites.filter((site) => site.id !== payload);
    });
  },
});

export default sitesSlice.reducer;

export const { resetSites } = sitesSlice.actions;
